import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import { Message } from 'semantic-ui-react';

import Search from '../views/Shared/Search';
import withPagination from './WithPagination';
import LoadingDataEnhancer from './LoadingData';
import GuaranteePurchasesList from '../views/CancelledGuaranteePurchases/List';
import GuaranteeReinstate from '../views/CancelledGuaranteePurchases/ReinstateGuaranteeForm';
import Pagination from '../views/Shared/Pagination';
import useMockGuarantees from '../hooks/useMockGuarantees';
import { useGuaranteeProduct } from '../hooks/useGuaranteePurchases';
import cancelledPurchasesQuery from '../graphql/cancelledPurchasesQuery';
import { setSearch } from '../data/searchField/actions';
import { setPage } from '../data/page/actions';
import reinstateGuaranteePurchaseMutation from '../graphql/reinstateGuaranteePurchaseMutation';
import { GUARANTEES_V2_ENABLED } from '../config';

const DEFAULT_PAGE_SIZE = 25;

// Mounted only when GUARANTEES_V2_ENABLED is true, so the hook and mock data
// are never instantiated in production.
const MockCancelledGuaranteePurchases = () => {
  const [selectedProduct] = useGuaranteeProduct();
  const [mockPage, setMockPage] = useState(1);
  const [localSearchFields, setLocalSearchFields] = useState({});

  const {
    data: cancelledPurchases,
    loading,
    isLastPage,
    start,
    end,
  } = useMockGuarantees({
    productFamily: selectedProduct || '',
    tab: 'cancelled',
    page: mockPage,
    searchFields: localSearchFields,
  });

  const [selectedGuaranteePurchase, setSelectedGuaranteePurchase] = useState(null);
  const [isReinstateModalOpen, setIsReinstateModalOpen] = useState(false);
  const [reinstateFormError, setReinstateFormError] = useState(null);
  const [reinstateFormSubmitting, setReinstateFormSubmitting] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);

  const handleMessageDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const onReinstateFormCloseHandler = useCallback(() => {
    setIsReinstateModalOpen(false);
    setSelectedGuaranteePurchase(null);
  }, []);

  const onSelectGuarantee = useCallback((action, guarantee) => {
    setSelectedGuaranteePurchase(guarantee);

    switch (action) {
      case 'Reinstate Guarantee':
        setIsReinstateModalOpen(true);
        setReinstateFormError(null);
        setReinstateFormSubmitting(false);
        break;
      default:
        setSelectedGuaranteePurchase(null);
    }
  }, []);

  const onReinstateFormSubmitHandler = useCallback(() => {
    setReinstateFormSubmitting(false);
    setIsReinstateModalOpen(false);
    setSelectedGuaranteePurchase(null);
    setFlashMessage('Successfully Reinstate Guarantee Purchase');
  }, []);

  const onSearchSubmitHandler = useCallback(event => {
    event.preventDefault();
    const { vin } = event.target;
    const fields = { vin: vin ? vin.value.trim() || null : null };
    setLocalSearchFields(fields);
    setMockPage(1);
  }, []);

  const nextHandler = useCallback(() => {
    if (!isLastPage) setMockPage(p => p + 1);
  }, [isLastPage]);

  const previousHandler = useCallback(() => {
    setMockPage(p => Math.max(1, p - 1));
  }, []);

  return (
    <div>
      {flashMessage && (
        <Message
          onDismiss={handleMessageDismiss}
          content={flashMessage}
          positive
        />
      )}
      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={{}}
        searchOptions={['vin']}
      />
      <GuaranteePurchasesList
        onSelectGuarantee={onSelectGuarantee}
        cancelledPurchases={cancelledPurchases}
      />
      <Pagination
        start={start}
        end={end}
        nextHandler={nextHandler}
        previousHandler={previousHandler}
        loading={loading}
        lastPage={isLastPage}
        pageSize={DEFAULT_PAGE_SIZE}
      />

      {selectedGuaranteePurchase && isReinstateModalOpen && (
        <div>
          <GuaranteeReinstate
            isReinstateModalOpen={isReinstateModalOpen}
            onCloseHandler={onReinstateFormCloseHandler}
            onSubmitHandler={onReinstateFormSubmitHandler}
            cancelledPurchase={selectedGuaranteePurchase}
            error={reinstateFormError}
            submitting={reinstateFormSubmitting}
          />
        </div>
      )}
    </div>
  );
};

const CancelledGuaranteePurchases = props => {
  if (GUARANTEES_V2_ENABLED) {
    return <MockCancelledGuaranteePurchases />;
  }

  const {
    data: { cancelledPurchases, refetch },
    searchFields,
    setSearchFields: setSearchFieldsAction,
    setPage: setPageAction,
    reinstateMutation,
  } = props;

  return (
    <LegacyCancelledGuaranteePurchases
      cancelledPurchases={cancelledPurchases}
      searchFields={searchFields}
      setSearchFieldsAction={setSearchFieldsAction}
      setPageAction={setPageAction}
      reinstateMutation={reinstateMutation}
      refetch={refetch}
    />
  );
};

const LegacyCancelledGuaranteePurchases = ({
  cancelledPurchases,
  searchFields,
  setSearchFieldsAction,
  setPageAction,
  reinstateMutation,
  refetch,
}) => {
  const [selectedGuaranteePurchase, setSelectedGuaranteePurchase] = useState(null);
  const [isReinstateModalOpen, setIsReinstateModalOpen] = useState(false);
  const [reinstateFormError, setReinstateFormError] = useState(null);
  const [reinstateFormSubmitting, setReinstateFormSubmitting] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);

  const handleMessageDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const onSelectGuarantee = useCallback((action, guarantee) => {
    setSelectedGuaranteePurchase(guarantee);

    switch (action) {
      case 'Reinstate Guarantee':
        setIsReinstateModalOpen(true);
        setReinstateFormError(null);
        setReinstateFormSubmitting(false);
        break;
      default:
        setSelectedGuaranteePurchase(null);
    }
  }, []);

  const onSearchSubmitHandler = useCallback(
    event => {
      const { vin } = event.target;
      const variableArguments = {
        vin: vin.value.trim() || null,
      };
      refetch({
        ...variableArguments,
      });

      setSearchFieldsAction(variableArguments);
      setPageAction({});
    },
    [refetch, setSearchFieldsAction, setPageAction]
  );

  const onReinstateFormCloseHandler = useCallback(() => {
    setIsReinstateModalOpen(false);
    setSelectedGuaranteePurchase(null);
  }, []);

  const onReinstateFormSubmitHandler = useCallback(
    values => {
      setReinstateFormSubmitting(true);

      const reinstateInput = { ...values };
      reinstateMutation({
        variables: { reinstateInput },
      })
        .then(({ data: mutationData }) => {
          const {
            reinstateGuaranteePurchase: { success, errors },
          } = mutationData;
          if (success) {
            refetch();
            setIsReinstateModalOpen(false);
            setSelectedGuaranteePurchase(null);
            setReinstateFormSubmitting(false);
            setFlashMessage('Successfully Reinstate Guarantee Purchase');
          } else {
            setReinstateFormSubmitting(false);
            setReinstateFormError(errors);
          }
        })
        .catch(() => {
          setReinstateFormSubmitting(false);
          setReinstateFormError('Error while reinstate Guarantee Purchase');
        });
    },
    [reinstateMutation, refetch]
  );

  return (
    <div>
      {flashMessage && (
        <Message
          onDismiss={handleMessageDismiss}
          content={flashMessage}
          positive
        />
      )}
      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={searchFields}
        searchOptions={['vin']}
      />
      <GuaranteePurchasesList
        onSelectGuarantee={onSelectGuarantee}
        cancelledPurchases={cancelledPurchases}
      />

      {selectedGuaranteePurchase && isReinstateModalOpen && (
        <div>
          <GuaranteeReinstate
            isReinstateModalOpen={isReinstateModalOpen}
            onCloseHandler={onReinstateFormCloseHandler}
            onSubmitHandler={onReinstateFormSubmitHandler}
            cancelledPurchase={selectedGuaranteePurchase}
            error={reinstateFormError}
            submitting={reinstateFormSubmitting}
          />
        </div>
      )}
    </div>
  );
};

LegacyCancelledGuaranteePurchases.propTypes = {
  cancelledPurchases: PropTypes.array,
  searchFields: PropTypes.object.isRequired,
  setSearchFieldsAction: PropTypes.func.isRequired,
  setPageAction: PropTypes.func.isRequired,
  reinstateMutation: PropTypes.func,
  refetch: PropTypes.func.isRequired,
};

LegacyCancelledGuaranteePurchases.defaultProps = {
  cancelledPurchases: [],
  reinstateMutation: null,
};

CancelledGuaranteePurchases.propTypes = {
  data: PropTypes.shape({
    cancelledPurchases: PropTypes.array,
    refetch: PropTypes.func,
  }),
  searchFields: PropTypes.object.isRequired,
  setSearchFields: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  reinstateMutation: PropTypes.func,
};

CancelledGuaranteePurchases.defaultProps = {
  data: {},
  reinstateMutation: null,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields },
  } = state;
  return {
    searchFields,
  };
};

const legacyHOCs = GUARANTEES_V2_ENABLED
  ? []
  : [
    withPagination(cancelledPurchasesQuery, 'cancelledPurchases'),
    graphql(reinstateGuaranteePurchaseMutation, {
      name: 'reinstateMutation',
    }),
    LoadingDataEnhancer,
  ];

export default compose(
  withRouter,
  ...legacyHOCs,
  connect(mapStateToProps, mapDispatchToProps)
)(CancelledGuaranteePurchases);
